import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import Lightbox, { LightboxActionButton, LightboxNotice } from 'components/Widget/Lightbox';
import { MenuItemAction } from 'menu/MenuItem';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveScene, selectFrame } from 'redux/db/reducers';
import {
	canEdit,
	findNextFramePosition,
	getFrameDisplayBounds,
	SearchDirection,
} from 'redux/db/util';
import {
	centerCanvasAroundSceneBoundingBox,
	setCenteredFrameId,
	setCenterFramesForChatId,
	setSelected,
} from 'redux/mode/actions';
import { Pick } from 'redux/mode/classes';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { frameImageUrl, frameImageUrlById } from 'utils';

// A frame that has been deleted still shows up in chat. Opening it here gives
// the two things worth doing with it — look at it, or put it back — instead of
// treating the click itself as consent to modify the canvas.

interface DeletedFrameLightboxProps {
	frameId: string;
	onClose: () => void;
}

const DeletedFrameLightbox = ({ frameId, onClose }: DeletedFrameLightboxProps) => {
	const dispatch = useDispatch();

	const { frame, scene, isMobile } = useSelector(
		(r: RootReducer) => ({
			frame: selectFrame(r.db, frameId),
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			isMobile: selectIsMobile(r.mode),
		}),
		shallowEqual
	);

	const isEditable = canEdit(scene);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onRestore = () => {
		if (!frame || !isEditable) return;

		// Try to put the frame back where it was; if that space is occupied, move
		// down until we find an empty one, so restoring never overlaps a frame
		// that is already on the canvas.
		const bounds = findNextFramePosition(getFrameDisplayBounds(frame), SearchDirection.DOWN);

		// The write applies optimistically and reverts itself if the server
		// refuses, so the reveal below can run against the frame straight away.
		// showToast is what makes that reversal legible rather than silent.
		forge.dbrUpdateFrame({
			update: {
				id: frame.id,
				deletedAt: null,
				x: bounds.x,
				y: bounds.y,
			},
			showToast: true,
		});

		onClose();

		if (isMobile) {
			dispatch(setCenteredFrameId(frame.id));
			return;
		}

		dispatch(setCenterFramesForChatId(null));
		dispatch(
			centerCanvasAroundSceneBoundingBox({
				boundingBox: bounds,
				animated: true,
				frameId: frame.id,
			})
		);
		dispatch(setSelected([new Pick(frame.id)]));
	};

	const onPreview = () => {
		onClose();
		MenuItemAction.createOpenPrototype(frameId).callback();
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	if (!frame) return null;

	const imageUrl = frameImageUrl(frame) ?? frameImageUrlById(frame.id);

	// The reason rides with the notice rather than a tooltip: a disabled action
	// has to explain itself where it is read.
	const notice = (
		<LightboxNotice>
			This frame was deleted from the canvas.
			{!isEditable && ' You have view-only access to this scene.'}
		</LightboxNotice>
	);

	const actions = (
		<>
			<LightboxActionButton $variant="secondary" onClick={onPreview}>
				<Icon type={IconType.PLAY} width={11} height={11} />
				Preview
			</LightboxActionButton>
			<LightboxActionButton
				$variant="primary"
				$disabled={!isEditable}
				disabled={!isEditable}
				onClick={onRestore}
			>
				Restore to canvas
			</LightboxActionButton>
		</>
	);

	return (
		<Lightbox
			items={[{ type: 'image', url: imageUrl }]}
			notice={notice}
			actions={actions}
			onClose={onClose}
		/>
	);
};

export default DeletedFrameLightbox;
